<script lang="ts" setup>
	import type { ListContext } from '~/components/Shared/list-item.vue'
	import type { CsvObjectContext } from '#imports'
	import type { BatchUploadSteps } from '@/components/Uploads/batch.vue'
	import useEmployeesApi from '~/services/apis/employeesApi'

	const props = defineProps<{
		planDetails: PlanInformationContext | null
		dialogTitle: string
		summary: ListContext
	}>()
	const csvResult = shallowRef<CsvObjectContext | null>()
	const fileToUpload = ref<FormData | null>(null)
	const guide = reactive<BatchUploadSteps>({
		'Start Here': {
			key: 'start',
			desc: 'Download the CSV/ XLS file template below'
		},
		'Fill Details': {
			key: 'details',
			desc: 'Fill in the CSV/ XLS file you just downloaded with information of staff you want to deactivate from your dashboard. Key contents are: <span class="text-gray-600 font-medium">First name,Last name,Email address, Phone number</span> of staff to be removed. Save it to your Desktop or any folder on your PC. See sample below:'
		},
		'Upload saved file ': {
			key: 'upload',
			desc: 'Upload or drag & drop the saved CSV/ XLS file you just filled on your PC.'
		}
	})
	const { removeEmployee } = useEmployeesApi()
	const { urlQuery } = useUtils()
	const { fetchStream, uploadProgress, pending: uploadingBatch } = useStreamReader()
	const computingRecords = shallowRef(false)
	const submittingRecords = shallowRef(false)
	const confirmDialog = shallowRef(false)
	const computedMetrics = shallowRef<StaffRemovalResponse | null>(null)
	const { internationalNoFormat } = useCurrency()
	const deactivateForm = reactive({
		terms: false,
		password: '',
		deactivation_type: ''
	})
	const rawFile = computed(() => {
		if (fileToUpload.value) {
			return fileToUpload.value.get('file') as File
		}
		return null
	})
	const needToKnow = computed(() => [
		`You are about to deactivate <span class="text-primary-2">${internationalNoFormat(
			computedMetrics.value?.total_staff_to_be_deactivated ?? 0
		)} staff </span> from their policies.`,
		'These staff will be removed from all plans that cover them, their dependants will also be removed.',
		'You can choose to deactivate a policy instantly or keep it till the end of the end of the first month renewal cycle.',
		'<span class="text-error-500 italic font-[700]">An instant deactivation</span> will deactivate the staff from the plans that cover them immediately.',
		'<span class="text-warning-500 italic font-[700]">Pending deactivation</span> will deactivate this staff policies at the end of the cover period.',
		'Staff whose policies have been deactivated will move to the section termed “<span class="text-primary-2 font-semibold">Deactivated policies</span>”.',
		'Deactivated policies can be added to your plan at any time.'
	])
	const removalSummary = computed<ListContext>(() => ({
		'Total staff to be deactivated': internationalNoFormat(
			computedMetrics.value?.total_staff_to_be_deactivated ?? 0
		),
		'Total staff now covered': internationalNoFormat(computedMetrics.value?.current_staff ?? 0)
	}))
	const deactivationMethod = computed(() => {
		return urlQuery.value?.method as 'batch' | 'manual'
	})
	const computeFileRecord = async (e?: FormData) => {
		try {
			computingRecords.value = true
			const response = await removeEmployee(deactivationMethod.value, {
				file: e,
				preview: true
			})
			if (response.value?.data) {
				computedMetrics.value = response.value.data
				confirmDialog.value = true
			}
		} finally {
			computingRecords.value = false
		}
	}

	const deactivateStaffPolicy = async () => {
		try {
			if (deactivationMethod.value === 'batch') {
				confirmDialog.value = false
				const batchResult = await fetchStream('/batch/remove-employees-policies', {
					body: fileToUpload.value,
					methods: 'DELETE',
					query: {
						is_immediate: deactivateForm.deactivation_type === 'instant'
					}
				})
				if (batchResult.data) {
					useNotification().alert({
						message: batchResult.responseText,
						status: batchResult.responseCode
					})
					deactivateForm.terms = false
					navigateTo(
						{
							name: 'mycover-plan_id',
							params: { plan_id: props.planDetails?.id ?? '' },
							query: { tab: 'staff_covered', status: 'deactivated' }
						},
						{ replace: true }
					)
				}
				return null
			}
		} catch (error) {
			console.error(error)
		}
	}
</script>

<template>
	<div>
		<employees-batch-uploads v-if="!uploadingBatch" v-model:file="fileToUpload" @change="computeFileRecord" />

		<uploads-progress v-else :file="rawFile" :progress="uploadProgress" class="mx-auto max-w-[700px]" />
		<loader-with-text :active="computingRecords" text="Processing file" />
		<employees-deactivate-policy-modal
			v-model="confirmDialog"
			:need-to-know="needToKnow"
			:submitting="submittingRecords"
			:removal-summary="removalSummary"
			use-emit
			@submit="deactivateStaffPolicy"
		/>
	</div>
</template>
